/**
 * An amount of money, as the chips it is made of.
 *
 * ============================== WHY THIS EXISTS ==================================
 * Phase 3 drew one chip top for any bet: a $560 bet was one disc, a $4,850 pot three.
 * The Phase 1 kit has seven photoreal colours with real denominations and the cap /
 * slice / tail pieces that tile into a stack of any height, and none of it was on the
 * felt. This turns an amount into what a dealer would build from it — largest chips
 * first, one stack per colour, ten high at most — and draws it with the kit's own
 * `ChipStack`, so what sits at a bet spot, under the pot pill and in flight is the same
 * picture drawn by the same component (rule 5).
 *
 * $560 is one blue (500), two greens (25) and two reds (5): three short stacks. $4,850
 * is four purples, a blue, three blacks and two greens: four stacks. Stacks stand side
 * by side with a small overlap, tallest on the left, bottoms level, so a bet reads as a
 * pile of money and the height of the pile is the size of the bet. An eleventh chip of a
 * colour starts a second stack of that colour beside the first.
 *
 * Whole chips only. Bucks are whole; a cash table's quarter-blind fractions are the
 * label's business, not the chips' — the number beside the pile carries the exact value.
 *
 * Sized in `--chip`, a `cqmin` token from `topdown.css`, like everything on the felt.
 * ===============================================================================
 */
import { ChipStack, CHIP_COLORS, chipDenom, type ChipColor } from "../art/chips";

/** One stack: a colour and how many chips of it. */
export interface Stack { color: ChipColor; count: number }

/** The most chips one stack holds before a second one starts beside it. */
export const STACK_MAX = 10;

/**
 * Break an amount into stacks, largest denomination first, in whole chips.
 *
 * `CHIP_COLORS` is ascending by denomination, so it is walked from the top. Anything
 * below the smallest chip is dropped (the label shows it). A zero or negative amount is
 * no stacks at all.
 */
export function breakdown(amount: number): Stack[] {
  const out: Stack[] = [];
  let left = Math.floor(amount + 1e-9);
  for (let i = CHIP_COLORS.length - 1; i >= 0 && left > 0; i--) {
    const color = CHIP_COLORS[i]!;
    const denom = chipDenom(color);
    if (denom <= 0) continue;
    let n = Math.floor(left / denom);
    left -= n * denom;
    while (n > 0) {
      const take = Math.min(STACK_MAX, n);
      out.push({ color, count: take });
      n -= take;
    }
  }
  return out;
}

/** Tallest on the left; equal heights keep denomination order. */
function arranged(stacks: Stack[]): Stack[] {
  return stacks.map((s, i) => ({ s, i })).sort((a, b) => b.s.count - a.s.count || a.i - b.i).map((x) => x.s);
}

/**
 * The pile.
 *
 * `width` is a CSS length for one chip's diameter; the default is the felt token. Pass
 * `stacks` to draw a pre-computed breakdown (the flight layer does, so what flies is
 * exactly what lands); pass `amount` to compute it here.
 */
export function ChipStacks({ amount, stacks, width = "var(--chip)", className }: {
  amount?: number;
  stacks?: Stack[];
  width?: string;
  className?: string;
}) {
  const list = arranged(stacks ?? breakdown(amount ?? 0));
  if (list.length === 0) return null;
  return (
    <span className={`td-chips${className ? ` ${className}` : ""}`} style={{ "--chip-w": width } as React.CSSProperties}
          aria-hidden="true">
      {list.map((s, i) => (
        <ChipStack key={`${s.color}${i}`} color={s.color} count={s.count} cssWidth="var(--chip-w)" />
      ))}
    </span>
  );
}
